import React, { ChangeEvent, Dispatch, FC, useCallback, useEffect, useRef } from 'react';
import { Card, CardBody, Col, FormGroup, Input, Label } from 'reactstrap';
import { FormNodeSimpleLinkedTermsModel } from '../../../../shared/dealer';
import { RichMarkdownIt, TermsLoader } from '../../../components';
import { useInputState, useUserSafe } from '../../../utils';
import { FormActionType } from '../../form/FormReducer';

interface FormNodeLinkTermsAgreementProps {
  readonly node: FormNodeSimpleLinkedTermsModel;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly dispatch: Dispatch<FormActionType>;
  readonly logRequiredCheckbox: (id: string, checked: boolean) => void;
}

export const SimpleLinkedTerms: FC<FormNodeLinkTermsAgreementProps> = ({
  node,
  checked,
  disabled,
  dispatch,
  logRequiredCheckbox,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const user = useUserSafe();
  const fullName = `${user.firstName!} ${user.lastName!}`;
  const [nameInput, setNameInput] = useInputState(disabled ? fullName : '');

  const onCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: 'setPolicyChecked',
        policy: node.termsKey,
        checked: e.target.checked,
      });

      if (node.requireChecked) {
        logRequiredCheckbox(node.id, e.target.checked);
      }
    },
    [dispatch, logRequiredCheckbox, node.id, node.requireChecked, node.termsKey],
  );

  useEffect(() => {
    if (node.agreementType !== 'name') {
      return;
    }

    dispatch({
      type: 'setPolicyChecked',
      policy: node.termsKey,
      checked: nameInput === fullName,
    });

    if (node.requireChecked) {
      logRequiredCheckbox(node.id, nameInput === fullName);
    }

    if (ref.current) {
      if (nameInput === fullName || nameInput.length === 0) {
        ref.current.setCustomValidity('');
      } else {
        ref.current.setCustomValidity(`Name does not match user profile (${fullName})`);
        ref.current.reportValidity();
      }
    }
  }, [dispatch, nameInput]);

  const inputId = `agreement-${node.agreementType}-${node.id}`;

  return (
    <Col className="col-12">
      {node.showPreview && node.termsKey !== 'none' && (
        <>
          <Card className="card-terms" color="secondary" outline>
            <CardBody>
              <TermsLoader name={node.termsKey} />
            </CardBody>
          </Card>
          <br />
        </>
      )}
      {node.agreementType === 'checkbox' && (
        <FormGroup check>
          <Input
            defaultChecked={checked}
            disabled={disabled}
            id={inputId}
            onChange={onCheckboxChange}
            required
            type="checkbox"
          />
          <Label check for={inputId}>
            <RichMarkdownIt source={node.displayName} />
          </Label>
        </FormGroup>
      )}
      {node.agreementType === 'name' && (
        <>
          <Input
            disabled={disabled}
            id={inputId}
            innerRef={ref}
            invalid={nameInput.length > 0 && nameInput !== fullName}
            onChange={setNameInput}
            required
            value={nameInput}
          />
          <Label for={inputId}>
            <RichMarkdownIt source={node.displayName} />
          </Label>
        </>
      )}
    </Col>
  );
};
